import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { App, Button, Space, Table, Tooltip, Typography } from 'antd';
import type { TableColumnsType, TablePaginationConfig } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';

import Permission from '@/components/permission';
import { formatNumber } from '@/utils/number';
import { handleExportResponse } from '@/utils/export-response';
import {
    featureDetailApi,
    featureDetailExportApi,
    featuresSummaryApi,
    type FeatureDetailItem,
    type FeatureKey,
    type FeatureSummaryItem,
    type FeaturesSummary,
} from '@/services/stats';

type SortBy = 'user_id' | 'times' | 'first_at' | 'last_at';
type SortOrder = 'asc' | 'desc';

const FEATURE_KEYS: FeatureKey[] = [
    'register',
    'watch',
    'report',
    'not_interested',
    'publish',
    'launch',
    'buy',
    'sell',
];

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 20;

const formatDateTime = (value: string | null): string => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '–');

const formatRate = (rate: number | null): string =>
    rate === null || rate === undefined ? '—' : `${(Number(rate) * 100).toFixed(1)}%`;

const PageFeaturesOverview: React.FC = () => {
    /**
     * Hooks
     */
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    /**
     * State
     */
    const [summary, setSummary] = useState<FeaturesSummary | null>(null);
    const [selectedFeature, setSelectedFeature] = useState<FeatureKey | null>(null);
    const [detailData, setDetailData] = useState<FeatureDetailItem[]>([]);
    const [detailTotal, setDetailTotal] = useState(0);
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [size, setSize] = useState(DEFAULT_SIZE);
    const [sortBy, setSortBy] = useState<SortBy>('times');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    /**
     * Requests
     */
    const { loading: summaryLoading, run: runSummary } = useRequest(featuresSummaryApi, {
        manual: true,
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg);
            }
            setSummary(data);
        },
    });

    const { loading: detailLoading, run: runDetail } = useRequest(featureDetailApi, {
        manual: true,
        onSuccess: ({ data: body }) => {
            const { code, data, msg, meta } = body;
            if (code !== 200) {
                return message.error(msg);
            }
            setDetailData(data ?? []);
            setDetailTotal(meta?.total ?? data?.length ?? 0);
        },
    });

    const { loading: exportLoading, run: runExport } = useRequest(featureDetailExportApi, {
        manual: true,
        onSuccess: ({ data: payload }) => {
            handleExportResponse(payload, formatMessage({ id: 'p.fo.detail.exportSuccess' }));
        },
        onError: (e: any) => {
            message.error(e?.response?.data?.msg || e?.message || 'Export failed');
        },
    });

    /**
     * Effects
     */
    useEffect(() => {
        runSummary();
    }, []);

    useEffect(() => {
        if (!selectedFeature) {
            return;
        }
        runDetail({
            feature: selectedFeature,
            page,
            size,
            sort_by: sortBy,
            sort_order: sortOrder,
        });
    }, [selectedFeature, page, size, sortBy, sortOrder]);

    /**
     * Summary table
     */
    const summaryRows = useMemo<FeatureSummaryItem[]>(() => {
        const byKey = new Map((summary?.features ?? []).map((item) => [item.feature, item]));
        return FEATURE_KEYS.map(
            (feature) =>
                byKey.get(feature) ?? {
                    feature,
                    participants: 0,
                    denominator: null,
                    rate: null,
                    denominator_metric: 'registered_users' as const,
                },
        );
    }, [summary]);

    const denominatorLabel = (metric: FeatureSummaryItem['denominator_metric']): string =>
        formatMessage({ id: `p.fo.denominatorMetric.${metric}` });

    const selectFeature = useCallback((record: FeatureSummaryItem) => {
        setSelectedFeature(record.feature);
        setPage(DEFAULT_PAGE);
        setSortBy('times');
        setSortOrder('desc');
    }, []);

    const summaryColumns = [
        {
            title: formatMessage({ id: 'p.fo.summary.column.feature' }),
            dataIndex: 'feature',
            key: 'feature',
            render: (feature: FeatureKey) => formatMessage({ id: `p.fo.feature.${feature}` }),
        },
        {
            title: formatMessage({ id: 'p.fo.summary.column.caliber' }),
            dataIndex: 'feature',
            key: 'caliber',
            render: (feature: FeatureKey) => formatMessage({ id: `p.fo.caliber.${feature}` }),
        },
        {
            title: formatMessage({ id: 'p.fo.summary.column.participants' }),
            dataIndex: 'participants',
            key: 'participants',
            align: 'right' as const,
            render: (value: number) => formatNumber(value, { decimalPlaces: 0 }),
        },
        {
            title: formatMessage({ id: 'p.fo.summary.column.denominator' }),
            dataIndex: 'denominator',
            key: 'denominator',
            align: 'right' as const,
            render: (_: unknown, record: FeatureSummaryItem) =>
                record.denominator === null || record.denominator === undefined ? (
                    <Tooltip title={formatMessage({ id: 'p.fo.summary.registerPvNote' })}>—</Tooltip>
                ) : (
                    `${formatNumber(record.denominator, { decimalPlaces: 0 })} (${denominatorLabel(record.denominator_metric)})`
                ),
        },
        {
            title: formatMessage({ id: 'p.fo.summary.column.rate' }),
            dataIndex: 'rate',
            key: 'rate',
            align: 'right' as const,
            render: (rate: number | null) =>
                rate === null || rate === undefined ? (
                    <Tooltip title={formatMessage({ id: 'p.fo.summary.registerPvNote' })}>{formatRate(rate)}</Tooltip>
                ) : (
                    formatRate(rate)
                ),
        },
        {
            title: formatMessage({ id: 'p.fo.summary.column.drilldown' }),
            key: 'drilldown',
            width: 120,
            render: (_: unknown, record: FeatureSummaryItem) => (
                <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        selectFeature(record);
                    }}
                >
                    <FormattedMessage id="p.fo.summary.drilldownAction" />
                </Button>
            ),
        },
    ];

    /**
     * Detail table
     */
    const columnSortOrder = useCallback(
        (field: SortBy): 'ascend' | 'descend' | undefined =>
            sortBy === field ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
        [sortBy, sortOrder],
    );

    const detailColumns: TableColumnsType<FeatureDetailItem> = [
        {
            title: formatMessage({ id: 'p.fo.detail.column.userId' }),
            dataIndex: 'user_id',
            key: 'user_id',
            width: 200,
            sorter: true,
            sortOrder: columnSortOrder('user_id'),
            render: (value: string) => <Typography.Text copyable>{value}</Typography.Text>,
        },
        {
            title: formatMessage({ id: 'p.fo.detail.column.wallet' }),
            dataIndex: 'wallet_address',
            key: 'wallet_address',
            width: 220,
            ellipsis: true,
            render: (value: string | null) =>
                value ? <Typography.Text copyable={{ text: value }}>{value}</Typography.Text> : '–',
        },
        {
            title: formatMessage({ id: 'p.fo.detail.column.times' }),
            dataIndex: 'times',
            key: 'times',
            width: 120,
            align: 'right',
            sorter: true,
            sortOrder: columnSortOrder('times'),
            render: (value: number) => formatNumber(value, { decimalPlaces: 0 }),
        },
        {
            title: formatMessage({ id: 'p.fo.detail.column.firstAt' }),
            dataIndex: 'first_at',
            key: 'first_at',
            width: 190,
            sorter: true,
            sortOrder: columnSortOrder('first_at'),
            render: formatDateTime,
        },
        {
            title: formatMessage({ id: 'p.fo.detail.column.lastAt' }),
            dataIndex: 'last_at',
            key: 'last_at',
            width: 190,
            sorter: true,
            sortOrder: columnSortOrder('last_at'),
            render: formatDateTime,
        },
    ];

    const onDetailChange = useCallback(
        (pagination: TablePaginationConfig, _filters: unknown, sorter: unknown) => {
            const sorterItem = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextSortBy = (sorterItem?.field as SortBy) ?? 'times';
            const nextSortOrder: SortOrder = sorterItem?.order === 'ascend' ? 'asc' : 'desc';
            if (nextSortBy !== sortBy || nextSortOrder !== sortOrder) {
                setSortBy(nextSortBy);
                setSortOrder(nextSortOrder);
            }
            setPage(pagination.current ?? DEFAULT_PAGE);
            setSize(pagination.pageSize ?? DEFAULT_SIZE);
        },
        [sortBy, sortOrder],
    );

    /**
     * Render
     */
    return (
        <Permission permission="Dashboard_Features_Overview">
            <>
                <div className="mb-16">
                    <Space size={16} wrap>
                        <Typography.Text type="secondary">
                            <FormattedMessage id="p.fo.summary.utcNote" />
                        </Typography.Text>
                        {summary?.end && (
                            <Typography.Text type="secondary">
                                {summary.start ? (
                                    <FormattedMessage
                                        id="p.fo.summary.window"
                                        values={{ start: summary.start, end: summary.end }}
                                    />
                                ) : (
                                    <FormattedMessage id="p.fo.summary.windowEnd" values={{ end: summary.end }} />
                                )}
                            </Typography.Text>
                        )}
                        <Typography.Text type="secondary">
                            <FormattedMessage id="p.fo.summary.registeredUsers" />：
                            {formatNumber(summary?.registered_users, { decimalPlaces: 0 })} ·{' '}
                            <FormattedMessage id="p.fo.summary.watchUsers" />：
                            {formatNumber(summary?.watch_users, { decimalPlaces: 0 })}
                        </Typography.Text>
                    </Space>
                </div>

                <ProCard className="mb-16" title={formatMessage({ id: 'p.fo.summary.title' })}>
                    <Table
                        columns={summaryColumns}
                        dataSource={summaryRows}
                        loading={summaryLoading}
                        onRow={(record) => ({
                            onClick: () => selectFeature(record),
                            style: { cursor: 'pointer' },
                        })}
                        rowClassName={(record) => (record.feature === selectedFeature ? 'ant-table-row-selected' : '')}
                        pagination={false}
                        rowKey="feature"
                        size="middle"
                    />
                </ProCard>

                <ProCard
                    title={`${formatMessage({ id: 'p.fo.detail.title' })}${
                        selectedFeature ? ` · ${formatMessage({ id: `p.fo.feature.${selectedFeature}` })}` : ''
                    }`}
                    extra={
                        selectedFeature ? (
                            <Space>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() =>
                                        runDetail({
                                            feature: selectedFeature,
                                            page,
                                            size,
                                            sort_by: sortBy,
                                            sort_order: sortOrder,
                                        })
                                    }
                                >
                                    <FormattedMessage id="p.fo.detail.refresh" />
                                </Button>
                                <Permission permission="Dashboard_Features_Overview_Export">
                                    <Button
                                        icon={<DownloadOutlined />}
                                        loading={exportLoading}
                                        type="primary"
                                        onClick={() =>
                                            runExport({
                                                feature: selectedFeature,
                                                sort_by: sortBy,
                                                sort_order: sortOrder,
                                            })
                                        }
                                    >
                                        <FormattedMessage id="p.fo.detail.export" />
                                    </Button>
                                </Permission>
                            </Space>
                        ) : null
                    }
                >
                    {selectedFeature ? (
                        <Table
                            columns={detailColumns}
                            dataSource={detailData}
                            loading={detailLoading}
                            onChange={onDetailChange}
                            pagination={{
                                current: page,
                                pageSize: size,
                                total: detailTotal,
                                showSizeChanger: true,
                                showTotal: (total) => `${total}`,
                            }}
                            rowKey={(record) => `${record.user_id}`}
                            size="middle"
                        />
                    ) : (
                        <Typography.Text type="secondary">
                            <FormattedMessage id="p.fo.detail.empty" />
                        </Typography.Text>
                    )}
                </ProCard>
            </>
        </Permission>
    );
};

export default PageFeaturesOverview;
